import React, { useState } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_YK_API_BASE || 'https://ykbaseapi.yugalkunj.org/ykrestapi';

const BulkEmail = () => {
  const [emailEntries, setEmailEntries] = useState([
    {
      id: 1,
      emailTo: '',
      userName: '',
      programName: '',
      attendee: '',
      orderNo: '',
      amount: '',
      phone: '',
      classDate: '',
      paymentType: '',
      billingAddress: '',
      paymentMode: ''
    }
  ]);
  
  const [extraMessage, setExtraMessage] = useState(
    `We have received your payment for the class you registered for. Due to a technical issue, the confirmation email was not sent earlier. Please consider this email as confirmation of both your payment and registration.

You may attend the class as scheduled on the specified date and time.

For any questions or assistance, please feel free to contact Richa Sinha (678-938-0638) for all music classes, or Anoop Dhingra (262-302-0539) for Gurukul and Hindi classes. We'll be happy to help.`
  );
  
  const [appName, setAppName] = useState('Class');
  const [templateName, setTemplateName] = useState('YKClassPayment');
  const [orgId, setOrgId] = useState('3');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState([]);
  const [jsonPasteText, setJsonPasteText] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [importing, setImporting] = useState(false);

  const addEmailEntry = () => {
    const newId = emailEntries.length > 0 ? Math.max(...emailEntries.map(e => e.id)) + 1 : 1;
    setEmailEntries([
      ...emailEntries,
      {
        id: newId,
        emailTo: '',
        userName: '',
        programName: '',
        attendee: '',
        orderNo: '',
        amount: '',
        phone: '',
        classDate: '',
        paymentType: '',
        billingAddress: '',
        paymentMode: ''
      }
    ]);
  };

  const removeEmailEntry = (id) => {
    if (emailEntries.length > 1) {
      setEmailEntries(emailEntries.filter(entry => entry.id !== id));
    }
  };

  const updateEmailEntry = (id, field, value) => {
    setEmailEntries(emailEntries.map(entry =>
      entry.id === id ? { ...entry, [field]: value } : entry
    ));
  };

  // Validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate JSON data structure
  const validateJsonData = (data) => {
    const errors = [];
    
    if (!Array.isArray(data)) {
      return { valid: false, errors: ['JSON must be an array of email objects'] };
    }
    
    if (data.length === 0) {
      return { valid: false, errors: ['JSON array is empty'] };
    }
    
    data.forEach((item, index) => {
      // Handle nested appContent structure or flat structure
      const content = item.appContent || item;
      const email = item.emailTo || content.email || '';
      if (!email) {
        errors.push(`Row ${index + 1}: Missing email address`);
      } else if (!isValidEmail(email)) {
        errors.push(`Row ${index + 1}: Invalid email format "${email}"`);
      }
    });
    
    return { valid: errors.length === 0, errors };
  };

  // Parse and import JSON data
  const parseAndImportJson = (jsonString) => {
    try {
      const jsonData = JSON.parse(jsonString);
      const validation = validateJsonData(jsonData);
      
      if (!validation.valid) {
        setJsonError(validation.errors.join('\n'));
        return false;
      }
      
      const importedEntries = jsonData.map((item, index) => {
        // Handle nested appContent structure (from API format) or flat structure
        const content = item.appContent || item;
        return {
          id: index + 1,
          emailTo: item.emailTo || content.email || '',
          userName: content.userName || content.name || '',
          programName: content.programName || content.className || '',
          attendee: content.attendee || content.students || '',
          orderNo: content.orderNo || content.transactionId || '',
          amount: content.amount || '',
          phone: content.phone || item.userPhoneNumber || '',
          classDate: content.headingDate || content.classDate || content.date || '',
          paymentType: content.paymentType || content.paymentMethod || '',
          billingAddress: content.billingAddress || content.address || '',
          paymentMode: content.paymentMode || ''
        };
      });
      
      setEmailEntries(importedEntries);
      setJsonError('');
      setJsonPasteText('');
      return true;
    } catch (error) {
      setJsonError('Invalid JSON: ' + error.message);
      return false;
    }
  };

  const handleJsonImport = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImporting(true);
      const reader = new FileReader();
      reader.onload = (event) => {
        parseAndImportJson(event.target.result);
        setImporting(false);
      };
      reader.onerror = () => {
        setJsonError('Failed to read file');
        setImporting(false);
      };
      reader.readAsText(file);
    }
  };

  const handleJsonPaste = () => {
    if (!jsonPasteText.trim()) {
      setJsonError('Please paste JSON data first');
      return;
    }
    setImporting(true);
    // Small delay to show processing indicator
    setTimeout(() => {
      parseAndImportJson(jsonPasteText);
      setImporting(false);
    }, 300);
  };

  const sendBulkEmails = async () => {
    const validEntries = emailEntries.filter(entry => entry.emailTo.trim());
    
    if (validEntries.length === 0) {
      alert('Please add at least one email recipient');
      return;
    }

    // Validate all email addresses before sending
    const invalidEmails = validEntries.filter(entry => !isValidEmail(entry.emailTo));
    if (invalidEmails.length > 0) {
      alert(`Invalid email addresses found:\n${invalidEmails.map(e => e.emailTo).join('\n')}\n\nPlease fix before sending.`);
      return;
    }

    setSending(true);
    setResults([]);
    const newResults = [];

    for (const entry of validEntries) {
      try {
        const emailParameter = {
          emailFrom: 'string',
          emailTo: entry.emailTo,
          emailSubject: 'string',
          emailBody: 'string',
          attachment: 'Y',
          userPhoneNumber: entry.phone || 'string',
          location: 'YKClassPayment',
          input: 'string',
          appContent: {
            // ##Name## and ##User## - User name (keep clean, appears twice in template)
            userName: entry.userName || 'Valued Customer',
            // ##CampName## - Class/Program name
            programName: entry.programName || 'Class',
            // ##CampSchedulerTable## - not used, keep as string
            appSchedule: 'As per schedule',
            // ##Students## - Enrolled students/attendees
            attendee: entry.attendee || 'N/A',
            // ##Date## - Order date
            date: new Date().toLocaleString(),
            // ##Type## - Order type
            orderType: 'Class Enrollment',
            // ##OrderNo## - Order/Transaction ID
            orderNo: entry.orderNo || 'N/A',
            // ##PaymentMode## - Payment mode
            paymentMode: entry.paymentMode || 'Credit Card',
            // ##Amount## - Payment amount
            amount: entry.amount || '0',
            // ##Email## - User email
            email: entry.emailTo,
            // ##Phone## - User phone (mapped from phone field)
            phone: entry.phone || 'N/A',
            // ##PickupPerson## - not used for class
            pickupPerson: 'N/A',
            // ##PickupPersonContact## - not used for class
            pickupPersonContact: 'N/A',
            // ##Instructions## - not used
            instructions: 'N/A',
            // ##ItemData## - not used
            itemData: 'N/A',
            // ##PickUp## - User Phone (template shows "User Phone: ##PickUp##")
            pickUpDate: entry.phone || 'N/A',
            // ##PickUptime## - not used
            pickUpTime: 'N/A',
            // ##PaymentType## - Payment method
            paymentType: entry.paymentType || 'Credit Card',
            // ##BillingAddress## - Billing address with extra message appended (appears only once at end)
            billingAddress: (entry.billingAddress || 'N/A') + '</p></li></ul><div style="white-space: pre-line; background-color: #fffde7; padding: 15px; border-left: 4px solid #ffc107; margin: 15px 0; border-radius: 4px;"><strong>Important Notice:</strong><br/><br/>' + extraMessage.replace(/\n/g, '<br/>') + '</div><ul style="display:none;"><li><p>',
            // ##Address## - not used
            address: 'N/A',
            // ##PickupOrReceiver## - not used
            pickupOrReceiver: 'N/A',
            // ##OrderReady## - not used
            orderReady: 'N/A',
            // ##OrderReadyLastLine## - not used
            orderReadyLastLine: 'N/A',
            // ##HeadingDate## - Class date
            headingDate: entry.classDate || entry.headingDate || 'As per schedule',
            // ##HeadingTime## - not used
            headingTime: 'N/A',
            startDate: new Date().toISOString(),
            endDate: new Date().toISOString()
          }
        };

        const response = await axios.post(
          `${API_BASE_URL}/MessageChannel/SendEmailMessage?appName=${appName}&templateName=${templateName}&orgId=${orgId}`,
          emailParameter
        );

        newResults.push({
          email: entry.emailTo,
          status: 'success',
          message: 'Email sent successfully',
          response: response.data
        });
      } catch (error) {
        newResults.push({
          email: entry.emailTo,
          status: 'error',
          message: error?.response?.data?.message || error.message || 'Failed to send email'
        });
      }
    }

    setResults(newResults);
    setSending(false);
  };

  const successCount = results.filter(r => r.status === 'success').length;
  const errorCount = results.filter(r => r.status === 'error').length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-12">
      <div className="container-custom max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-semibold text-gray-700 mb-2">
            📧 Bulk Email Sender
          </h1>
          <p className="text-sm text-gray-500">
            Send payment confirmation emails to multiple recipients
          </p>
        </div>

        {/* API Configuration */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border-2 border-gray-100">
          <h2 className="text-lg font-bold text-gray-800 mb-4">API Configuration</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">App Name</label>
              <input
                type="text"
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-400 focus:outline-none transition"
                placeholder="Class"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Template Name</label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-400 focus:outline-none transition"
                placeholder="YKClassPayment"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Org ID</label>
              <input
                type="text"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-400 focus:outline-none transition"
                placeholder="3"
              />
            </div>
          </div>
        </div>

        {/* Extra Message Section */}
        <div className="bg-green-50 rounded-2xl shadow-lg p-6 mb-6 border-2 border-green-100">
          <h2 className="text-lg font-bold text-green-800 mb-4">📝 Extra Message (Custom Text)</h2>
          <textarea
            value={extraMessage}
            onChange={(e) => setExtraMessage(e.target.value)}
            rows={8}
            className="w-full px-4 py-3 border-2 border-green-200 rounded-lg focus:border-green-400 focus:outline-none transition resize-y bg-white"
            placeholder="Enter additional message to include in the email..."
          />
          <p className="text-xs text-green-600 mt-2">
            <strong>Note:</strong> This message will be passed through multiple template fields (##Instructions##, ##CampSchedulerTable##, ##ItemData##, ##Address##, ##PickupOrReceiver##, ##OrderReadyLastLine##). If your email template uses any of these placeholders, the extra message will appear there.
          </p>
        </div>

        {/* Import JSON */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border-2 border-gray-100">
          <h2 className="text-lg font-bold text-gray-800 mb-4">📥 Import Email List (JSON)</h2>
          
          {/* File Import */}
          <div className="flex items-center gap-4 mb-4">
            <label className={`bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold py-2 px-6 rounded-full transition ${
              (importing || sending) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-lg'
            }`}>
              <input
                type="file"
                accept=".json"
                onChange={handleJsonImport}
                className="hidden"
                disabled={importing || sending}
              />
              📁 Import JSON File
            </label>
            <span className="text-sm text-gray-400">OR paste JSON below</span>
          </div>

          {/* JSON Paste Textarea */}
          <div className="mb-4">
            <textarea
              value={jsonPasteText}
              onChange={(e) => {
                setJsonPasteText(e.target.value);
                setJsonError('');
              }}
              rows={6}
              disabled={importing || sending}
              className={`w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-400 focus:outline-none transition resize-y font-mono text-sm ${
                (importing || sending) ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              placeholder={`Paste JSON array here, e.g.:
[
  { "emailTo": "user1@email.com", "userName": "John Doe", "programName": "Harmonium Class", "attendee": "Student 1", "orderNo": "1234", "amount": "100", "phone": "1234567890", "classDate": "Jan 25, 2026", "paymentType": "Credit Card" }
]`}
            />
          </div>

          {/* Error Display */}
          {jsonError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600 font-medium">❌ Validation Errors:</p>
              <pre className="text-xs text-red-500 mt-1 whitespace-pre-wrap">{jsonError}</pre>
            </div>
          )}

          {/* Import Button */}
          <div className="flex gap-3 items-center">
            <button
              onClick={handleJsonPaste}
              disabled={!jsonPasteText.trim() || importing || sending}
              className={`bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold py-2 px-6 rounded-full transition shadow-sm hover:shadow-md flex items-center gap-2 ${
                (!jsonPasteText.trim() || importing || sending) ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'
              }`}
            >
              {importing ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>✓ Validate & Import JSON</>
              )}
            </button>
            {jsonPasteText && !importing && (
              <button
                onClick={() => {
                  setJsonPasteText('');
                  setJsonError('');
                }}
                className="text-gray-500 hover:text-gray-700 font-medium py-2 px-4"
              >
                Clear
              </button>
            )}
          </div>

          {/* Format Help */}
          <p className="text-xs text-gray-500 mt-3">
            <strong>Accepted fields:</strong> emailTo, userName, programName, attendee, orderNo, amount, phone, classDate, paymentType, billingAddress, paymentMode
          </p>
        </div>

        {/* Email Entries */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border-2 border-gray-100">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold text-gray-800">
              Email Recipients ({emailEntries.length})
            </h2>
            <button
              onClick={addEmailEntry}
              className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-full transition shadow-sm hover:shadow-md text-sm"
            >
              + Add Recipient
            </button>
          </div>

          <div className="space-y-4 max-h-96 overflow-y-auto">
            {emailEntries.map((entry, index) => (
              <div
                key={entry.id}
                className="bg-gray-50 rounded-xl p-4 border border-gray-200"
              >
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-semibold text-gray-600">
                    Recipient #{index + 1}
                  </span>
                  {emailEntries.length > 1 && (
                    <button
                      onClick={() => removeEmailEntry(entry.id)}
                      className="text-red-500 hover:text-red-700 text-sm font-medium"
                    >
                      ✕ Remove
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Email To *</label>
                    <input
                      type="email"
                      value={entry.emailTo}
                      onChange={(e) => updateEmailEntry(entry.id, 'emailTo', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 focus:outline-none text-sm"
                      placeholder="recipient@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">User Name</label>
                    <input
                      type="text"
                      value={entry.userName}
                      onChange={(e) => updateEmailEntry(entry.id, 'userName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 focus:outline-none text-sm"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Program/Class Name</label>
                    <input
                      type="text"
                      value={entry.programName}
                      onChange={(e) => updateEmailEntry(entry.id, 'programName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 focus:outline-none text-sm"
                      placeholder="Harmonium & Vocal"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Attendee</label>
                    <input
                      type="text"
                      value={entry.attendee}
                      onChange={(e) => updateEmailEntry(entry.id, 'attendee', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 focus:outline-none text-sm"
                      placeholder="Neil Joshi"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Order No</label>
                    <input
                      type="text"
                      value={entry.orderNo}
                      onChange={(e) => updateEmailEntry(entry.id, 'orderNo', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 focus:outline-none text-sm"
                      placeholder="3133"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Amount</label>
                    <input
                      type="text"
                      value={entry.amount}
                      onChange={(e) => updateEmailEntry(entry.id, 'amount', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 focus:outline-none text-sm"
                      placeholder="120.00"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Phone</label>
                    <input
                      type="text"
                      value={entry.phone}
                      onChange={(e) => updateEmailEntry(entry.id, 'phone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 focus:outline-none text-sm"
                      placeholder="9819155423"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Send Button */}
        <div className="text-center mb-6">
          <button
            onClick={sendBulkEmails}
            disabled={sending || importing}
            className={`bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold py-4 px-12 rounded-full text-lg shadow-lg hover:shadow-xl transition-all duration-300 ${
              (sending || importing) ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'
            }`}
          >
            {sending ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Sending Emails...
              </span>
            ) : (
              `📤 Send ${emailEntries.filter(e => e.emailTo.trim()).length} Email(s)`
            )}
          </button>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-gray-100">
            <h2 className="text-lg font-bold text-gray-800 mb-4">
              Results: {successCount} Sent, {errorCount} Failed
            </h2>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {results.map((result, index) => (
                <div
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    result.status === 'success'
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-red-50 border border-red-200'
                  }`}
                >
                  <span className="text-sm font-medium text-gray-700">
                    {result.email}
                  </span>
                  <span
                    className={`text-sm font-semibold ${
                      result.status === 'success' ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {result.status === 'success' ? '✓ Sent' : `✕ ${result.message}`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sample JSON Format */}
        <div className="mt-6 bg-gray-800 rounded-2xl p-6 text-white">
          <h3 className="text-lg font-bold mb-3">Sample JSON Format for Import</h3>
          <pre className="text-sm overflow-x-auto bg-gray-900 p-4 rounded-lg">
{`[
  {
    "emailTo": "pankajsharma151280@gmail.com",
    "userName": "Garima Joshi",
    "programName": "Harmonium & Vocal - Beginner - Wednesday - Virtual",
    "attendee": "Neil Joshi",
    "orderNo": "3133",
    "amount": "120.00",
    "phone": "9819155423",
    "classDate": "Jan 25, 2026",
    "paymentType": "Credit Card",
    "billingAddress": "123 Main St, City, State 12345",
    "paymentMode": "Online"
  },
  {
    "emailTo": "another@email.com",
    "userName": "Another User",
    "programName": "Dance Class",
    "attendee": "Student Name",
    "orderNo": "3134",
    "amount": "150.00",
    "phone": "1234567890",
    "classDate": "Jan 26, 2026",
    "paymentType": "ACH",
    "billingAddress": "456 Oak Ave, Town, State 67890",
    "paymentMode": "Online"
  }
]`}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default BulkEmail;
